import React, { useState } from 'react';
import { Calendar, MapPin, Users, DollarSign, Tag, CreditCard, CheckCircle, Clock, AlertTriangle, XCircle, ArrowRight, MessageSquare, ChevronDown } from 'lucide-react';
import { formatDateTime } from '../../utils/dateFormatter';

/**
 * EventSummaryCard — Unified top-of-chat card.
 * Combines event context + payment summary into a single container.
 * One optional expand for payment breakdown. CTA always visible.
 */
const EventSummaryCard = ({ eventSummary, inquiry, booking, viewerRole, onPayNow, onNavigateToEvent }) => {
  const [expanded, setExpanded] = useState(false);

  const isAutoCreated = eventSummary?.auto_created;
  const eventType = inquiry?.event_type || eventSummary?.event_type || 'Event';
  const eventDate = inquiry?.event_date || eventSummary?.event_date;
  const guestCount = inquiry?.guest_count || eventSummary?.guest_count;
  const location = inquiry?.location || (eventSummary?.location_city && eventSummary?.location_zip
    ? `${eventSummary.location_city}, ${eventSummary.location_zip}`
    : eventSummary?.location_city || eventSummary?.location_zip || '');
  // Information Boundary Engine (defense-in-depth) — the customer's total
  // event budget is internal to Celey and must never render to vendors. The
  // /api/conversation/{inquiry_id}/context endpoint already omits `budget`
  // when viewer_role==='vendor'; this client predicate exists strictly as a
  // second layer in case future API shape drift accidentally re-exposes the
  // field. Customer and admin viewers continue to see the budget pill.
  const budget = (viewerRole === 'vendor') ? null : eventSummary?.budget;
  const services = eventSummary?.selected_services || [];

  // Payment state
  const hasBooking = !!booking;
  const depositPaid = booking?.deposit_paid;
  const finalPaid = booking?.final_paid;
  const depositAmount = booking?.deposit_amount || 0;
  const finalAmount = booking?.final_amount || 0;
  const agreedPrice = booking?.agreed_price || 0;
  const windowExpired = booking?.payment_window_status === 'expired';
  const timeRemaining = booking?.time_remaining_seconds;
  const bookingStatus = booking?.status;
  const isCancelled = ['cancelled', 'cancelled_by_customer', 'cancelled_by_vendor'].includes(bookingStatus);
  const isComplete = bookingStatus === 'completed';
  const lifecycle = booking?.lifecycle;
  const depDue = hasBooking && !depositPaid;
  const finalDue = hasBooking && depositPaid && !finalPaid;
  const depExpired = lifecycle?.deposit?.state === 'expired';
  const finOverdue = lifecycle?.final_payment?.state === 'overdue';
  const hasActionablePayment = viewerRole === 'customer' && onPayNow && hasBooking && !isCancelled && !isComplete;

  const fmt = (n) => `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
  const formatTime = (seconds) => {
    if (!seconds || seconds <= 0) return 'Expired';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
  };

  // Payment summary line
  const getPaymentSummary = () => {
    if (!hasBooking) return null;
    if (isCancelled) return { text: 'Booking cancelled', icon: XCircle, color: 'text-red-600' };
    if (isComplete || (depositPaid && finalPaid)) return { text: `Fully paid — ${fmt(agreedPrice)}`, icon: CheckCircle, color: 'text-emerald-600' };
    if (finalDue && finOverdue) return { text: `Final payment overdue — ${fmt(finalAmount)}`, icon: AlertTriangle, color: 'text-red-600' };
    if (finalDue) {
      const countdown = lifecycle?.final_payment?.countdown;
      return { text: `Final payment — ${fmt(finalAmount)}${countdown ? ` · ${countdown}` : ''}`, icon: Clock, color: 'text-amber-700' };
    }
    if (depDue && depExpired) {
      const countdown = lifecycle?.deposit?.countdown;
      const timeAgo = countdown && countdown !== 'Expired' ? countdown.replace('Expired · ', '') : '';
      return { text: `Deposit window expired${timeAgo ? ` · ${timeAgo}` : ''}`, icon: AlertTriangle, color: 'text-red-600' };
    }
    if (depDue) {
      const timeStr = timeRemaining > 0 ? ` · ${formatTime(timeRemaining)} left` : '';
      return { text: `Deposit due — ${fmt(depositAmount)}${timeStr}`, icon: Clock, color: 'text-amber-700' };
    }
    return { text: `Booking — ${fmt(agreedPrice)}`, icon: CreditCard, color: 'text-slate-600' };
  };

  const paymentSummary = getPaymentSummary();
  const PaymentIcon = paymentSummary?.icon;

  return (
    <div
      data-testid="event-summary-card"
      className="mx-3 mt-3 mb-1 rounded-xl border border-slate-200/80 bg-gradient-to-br from-slate-50 to-white shadow-sm overflow-hidden"
    >
      {/* ── Event Info ── */}
      <div className="px-4 py-3">
        <div className="flex items-start justify-between gap-2 mb-2">
          <button
            type="button"
            onClick={onNavigateToEvent || undefined}
            className={`text-sm font-semibold leading-tight truncate text-left ${onNavigateToEvent ? 'text-slate-800 hover:text-emerald-700 hover:underline cursor-pointer' : 'text-slate-800'}`}
            data-testid="event-summary-event-link"
          >
            {isAutoCreated ? 'Direct Inquiry' : eventType}
          </button>
          {budget && (
            <span className="flex items-center gap-1 text-xs text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full font-medium shrink-0">
              <DollarSign className="w-3 h-3" />
              {Number(budget).toLocaleString()}
            </span>
          )}
        </div>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs text-slate-600">
          {eventDate && (
            <span className="flex items-center gap-1.5 truncate">
              <Calendar className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              {formatDateTime ? formatDateTime(eventDate) : new Date(eventDate).toLocaleDateString()}
            </span>
          )}
          {location && (
            <span className="flex items-center gap-1.5 truncate">
              <MapPin className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              {location}
            </span>
          )}
          {guestCount && (
            <span className="flex items-center gap-1.5">
              <Users className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              {guestCount} guests
            </span>
          )}
          {services.length > 0 && (
            <span className="flex items-center gap-1.5 truncate">
              <Tag className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              {services.slice(0, 2).join(', ')}{services.length > 2 ? ` +${services.length - 2}` : ''}
            </span>
          )}
        </div>
        {inquiry?.special_requests && (
          <p className="mt-2 text-xs text-slate-500 leading-relaxed line-clamp-2 border-t border-slate-100 pt-2">
            {inquiry.special_requests}
          </p>
        )}
      </div>

      {/* ── Payment Section (only when booking exists) ── */}
      {paymentSummary && (
        <>
          {/* Divider */}
          <div className="border-t border-slate-200/60" />

          {/* Collapsed payment summary + expand toggle */}
          <button
            data-testid="payment-status-toggle"
            onClick={() => setExpanded(!expanded)}
            className="w-full px-4 py-2 flex items-center gap-2 text-left hover:bg-black/[0.02] transition-colors"
          >
            <PaymentIcon className={`w-3.5 h-3.5 shrink-0 ${paymentSummary.color}`} />
            <span className={`text-xs font-medium flex-1 ${paymentSummary.color}`}>{paymentSummary.text}</span>
            <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} />
          </button>

          {/* Expanded breakdown */}
          {expanded && !isCancelled && (
            <div className="px-4 pb-3 space-y-1.5 border-t border-slate-100 pt-2">
              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-500">Total agreed</span>
                <span className="font-semibold text-slate-700">{fmt(agreedPrice)}</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-1.5">
                  {depositPaid ? <CheckCircle className="w-3 h-3 text-emerald-500" /> : windowExpired ? <AlertTriangle className="w-3 h-3 text-amber-500" /> : <Clock className="w-3 h-3 text-slate-400" />}
                  <span className={depositPaid ? 'text-emerald-700' : 'text-slate-600'}>Deposit</span>
                </div>
                <span className={`font-medium ${depositPaid ? 'text-emerald-600' : 'text-slate-700'}`}>
                  {fmt(depositAmount)}{depositPaid ? ' Paid' : ''}
                </span>
              </div>
              {!booking?.requires_full_payment && (
                <div className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-1.5">
                    {finalPaid ? <CheckCircle className="w-3 h-3 text-emerald-500" /> : <Clock className="w-3 h-3 text-slate-400" />}
                    <span className={finalPaid ? 'text-emerald-700' : 'text-slate-600'}>Final payment</span>
                  </div>
                  <span className={`font-medium ${finalPaid ? 'text-emerald-600' : 'text-slate-700'}`}>
                    {fmt(finalAmount)}{finalPaid ? ' Paid' : ''}
                  </span>
                </div>
              )}
              {depDue && !isCancelled && timeRemaining != null && timeRemaining > 0 && (
                <div className="mt-1 flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50 rounded-lg px-2.5 py-1.5">
                  <Clock className="w-3 h-3 shrink-0" />
                  <span>Payment window: <strong>{formatTime(timeRemaining)}</strong> remaining</span>
                </div>
              )}
            </div>
          )}
          {expanded && isCancelled && booking?.cancellation_reason && (
            <div className="px-4 pb-3 border-t border-red-100 pt-2">
              <p className="text-xs text-red-600">{booking.cancellation_reason}</p>
            </div>
          )}

          {/* Single CTA — always visible when actionable */}
          {hasActionablePayment && (() => {
            if (depDue && depExpired) {
              return (
                <div data-testid="payment-card-expired-msg" className="w-full flex items-center justify-center gap-1.5 px-4 py-2.5 text-xs font-semibold text-red-700 bg-red-50 border-t border-red-100">
                  <MessageSquare className="w-3.5 h-3.5" /> Contact vendor to continue booking
                </div>
              );
            }
            if (depDue && !depExpired) {
              return (
                <button
                  data-testid="payment-card-pay-deposit-btn"
                  onClick={() => onPayNow(booking.id)}
                  className="w-full flex items-center justify-center gap-1.5 px-4 py-2.5 text-xs font-semibold text-white bg-[#C6A96B] hover:bg-[#B89655] transition-colors border-t border-gray-100"
                >
                  <CreditCard className="w-3.5 h-3.5" /> Complete Payment <ArrowRight className="w-3.5 h-3.5" />
                </button>
              );
            }
            if (finalDue && finOverdue) {
              return (
                <div data-testid="payment-card-overdue-msg" className="w-full flex items-center justify-center gap-1.5 px-4 py-2.5 text-xs font-semibold text-red-700 bg-red-50 border-t border-red-100">
                  <MessageSquare className="w-3.5 h-3.5" /> Message vendor to proceed
                </div>
              );
            }
            if (finalDue) {
              return (
                <button
                  data-testid="payment-card-pay-final-btn"
                  onClick={() => onPayNow(booking.id)}
                  className="w-full flex items-center justify-center gap-1.5 px-4 py-2.5 text-xs font-semibold text-white bg-[#C6A96B] hover:bg-[#B89655] transition-colors border-t border-gray-100"
                >
                  <CreditCard className="w-3.5 h-3.5" /> Complete Payment <ArrowRight className="w-3.5 h-3.5" />
                </button>
              );
            }
            return null;
          })()}
        </>
      )}
    </div>
  );
};

export default EventSummaryCard;
